import React from "react";
import "../../People.css";
import "../../assets/css/people.css";
import { getAllPrograms } from "../../programs/programIndex";
import mtechPrograms from "../../programDetails/mtechPrograms.json";
import onlineMtechPrograms from "../../programDetails/onlineMtechPrograms.json";
import mscPrograms from "../../programDetails/mscPrograms.json";
import { FaEnvelope } from "react-icons/fa";
import avinashPhoto from "../../assets/images/people/AvinashEranki.jpg";
import subhradeepPhoto from "../../assets/images/people/Subhradeep_Chatterjee.jpg";
import anurupPhoto from "../../assets/images/people/Anurup_Datta.jpg";
import rajalakshmiPhoto from "../../assets/images/people/rajalakshmi_p.jpg";
import renuPhoto from "../../assets/images/people/renu.png";
import nithyanandanPhoto from "../../assets/images/people/k-nithyanandan.jpeg";
import vanjariPhoto from "../../assets/images/people/Siva_Rama_Krishna_Vanjari.jpg";
import mayurPhoto from "../../assets/images/people/mayur.jpg";
import saswataPhoto from "../../assets/images/people/Saswata_Bhattacharya.jpg";
import prabhatPhoto from "../../assets/images/people/prabhat-kumar.jpg";
import saurabhPhoto from "../../assets/images/people/saurabh-s.jpg";
import harishPhoto from "../../assets/images/people/harish.jpeg";

const programDetailsById = [
  mtechPrograms,
  onlineMtechPrograms,
  mscPrograms,
].reduce((details, dataset) => {
  Object.entries(dataset).forEach(([key, program]) => {
    details[program.id || key] = program;
  });
  return details;
}, {});

const branchByEmailCode = {
  bme: "Biomedical Engineering",
  ce: "Civil Engineering",
  ee: "Electrical Engineering",
  mae: "Mechanical and Aerospace Engineering",
  msme: "Materials Science and Metallurgical Engineering",
  phy: "Physics",
};

const normalizedPhotoByName = {
  "anurup datta": anurupPhoto,
  "avinash eranki": avinashPhoto,
  "mayur vaidya": mayurPhoto,
  "nithyanandan k": nithyanandanPhoto,
  "p rajalakshmi": rajalakshmiPhoto,
  "prabhat kumar": prabhatPhoto,
  "renu john": renuPhoto,
  "saswata bhattacharya": saswataPhoto,
  "saurabh sandilya": saurabhPhoto,
  "shiva rama krishna vanjari": vanjariPhoto,
  "subhradeep chatterjee": subhradeepPhoto,
};

const cleanName = (name) =>
  name
    .replace(/^(dr\.?|prof\.?|professor|mr\.?|ms\.?)\s+/i, "")
    .replace(/\s*\([^)]*\)\s*/g, " ")
    .replace(/,\s*[a-z. ]+$/i, "")
    .replace(/\s+/g, " ")
    .trim()
    .replace("Saswata Battacharya", "Saswata Bhattacharya");

const normalizeName = (name) =>
  cleanName(name)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const getInitials = (name) =>
  cleanName(name)
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");

const getBranchFromEmail = (email, fallbackDepartment) => {
  const domainCode = email?.split("@")[1]?.split(".")[0]?.toLowerCase();
  if (domainCode && branchByEmailCode[domainCode]) {
    return branchByEmailCode[domainCode];
  }
  if (fallbackDepartment) {
    return fallbackDepartment
      .replace(/^Department of\s+/i, "")
      .replace(/^Department:\s*/i, "")
      .replace(/\bBME\b/g, "Biomedical Engineering")
      .replace(/\bMAE\b/g, "Mechanical and Aerospace Engineering")
      .replace(/\bMSME\b/g, "Materials Science and Metallurgical Engineering")
      .replace(/\bEE\b/g, "Electrical Engineering")
      .replace(/\bPHY\b/g, "Physics")
      .trim();
  }
  return "Centre for Interdisciplinary Programs";
};

const getShortProgramName = (programName) =>
  programName
    .replace(/^Online M\.Tech in\s+/i, "")
    .replace(/^M\.Tech in\s+/i, "")
    .replace(/^M\.Sc in\s+/i, "")
    .trim();

const getPhotoForName = (name) => normalizedPhotoByName[normalizeName(name)];

const mergeTeamMembers = (members) => {
  const orderedMembers = [];
  const membersByName = new Map();

  members.forEach((member) => {
    const key = normalizeName(member.name);
    const existing = membersByName.get(key);

    if (!existing) {
      const nextMember = {
        ...member,
        posts: [member.post],
      };
      orderedMembers.push(nextMember);
      membersByName.set(key, nextMember);
      return;
    }

    if (!existing.posts.includes(member.post)) {
      existing.posts.push(member.post);
    }
    if (!existing.photo && member.photo) {
      existing.photo = member.photo;
    }
    if (
      existing.branch === "Centre for Interdisciplinary Programs" &&
      member.branch !== "Centre for Interdisciplinary Programs"
    ) {
      existing.branch = member.branch;
    }
  });

  return orderedMembers.map(({ posts, ...member }) => ({
    ...member,
    post: posts.join("; "),
  }));
};

const coordinatorMembers = getAllPrograms().flatMap((program) => {
  const programDetails = programDetailsById[program.id];
  const coordinators = programDetails?.people?.coordinators || [];

  return coordinators.map((coordinator) => {
    const name = cleanName(coordinator.name);
    return {
      name,
      branch: getBranchFromEmail(coordinator.email, coordinator.department),
      post: `Coordinator, ${getShortProgramName(program.name)}`,
      email: coordinator.email,
      photo: getPhotoForName(name),
    };
  });
});

const teamMembers = mergeTeamMembers([
  {
    name: "Harish N Dixit",
    branch: "Mechanical and Aerospace Engineering",
    post: "Chair, CIP",
    email: "chair@cip.iith.ac.in",
    photo: harishPhoto,
  },
  {
    name: "Avinash Eranki",
    branch: "Biomedical Engineering",
    post: "DPGC",
    email: "aeranki@bme.iith.ac.in",
    photo: avinashPhoto,
  },
  ...coordinatorMembers,
]);

const staffMembers = [
  {
    name: "Ms. Veena Bairy",
    branch: "Office CIP",
    email: "office@cip.iith.ac.in",
  },
];

const TeamPhoto = ({ member }) => {
  if (member.photo) {
    return <img src={member.photo} alt={member.name} className="our-team-photo" />;
  }

  return (
    <div className="our-team-photo our-team-photo-fallback" role="img" aria-label={member.name}>
      {getInitials(member.name)}
    </div>
  );
};

const TeamCard = ({ member }) => (
  <article className="our-team-member">
    <div className="our-team-photo-frame">
      <TeamPhoto member={member} />
    </div>
    <div className="our-team-details">
      <h2 className="our-team-name">{member.name}</h2>
      {member.branch && <p className="our-team-branch">{member.branch}</p>}
      {member.post && <p className="our-team-post">{member.post}</p>}
      <a className="our-team-email" href={`mailto:${member.email}`}>
        <FaEnvelope className="our-team-email-icon" aria-hidden="true" />
        {member.email}
      </a>
    </div>
  </article>
);

const Staff = () => {
  return (
    <main className="people-container staff-page">
      <section className="our-team-section">
        <h1 className="section-heading">Our Team</h1>
        <div className="our-team-grid">
          {teamMembers.map((member) => (
            <TeamCard member={member} key={`${member.name}-${member.email}`} />
          ))}
        </div>

        <div className="our-team-staff-block">
          <h2 className="section-heading staff-subheading">Staff</h2>
          <div className="our-team-grid staff-team-grid">
            {staffMembers.map((member) => (
              <TeamCard member={member} key={`${member.name}-${member.email}`} />
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default Staff;
